import { endOfWeek, format, isAfter, isBefore, startOfWeek } from 'date-fns';
import { useDeferredValue, useMemo } from 'react';
import type { Item } from '@/lib/api/types/items';

export const useTimelineMinimapDates = ({
  from,
  to,
  items,
}: {
  from: string | null;
  to: string | null;
  items: Item[];
}) => {
  const deferredFrom = useDeferredValue(from);
  const deferredTo = useDeferredValue(to);

  const { firstItemWeekStartDate, lastItemWeekEndDate } = useMemo(() => {
    let firstDate: Date | null = null;
    let lastDate: Date | null = null;

    for (const item of items) {
      if (!item.targetStartDateTime || !item.targetFinishDateTime) continue;
      const start = new Date(item.targetStartDateTime);
      const end = new Date(item.targetFinishDateTime);
      if (!firstDate || start < firstDate) firstDate = start;
      if (!lastDate || end > lastDate) lastDate = end;
    }

    return {
      firstItemWeekStartDate: firstDate
        ? format(startOfWeek(firstDate, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        : null,
      lastItemWeekEndDate: lastDate
        ? format(endOfWeek(lastDate, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        : null,
    };
  }, [items]);

  const formattedFirstItemDate = firstItemWeekStartDate ?? (deferredFrom || '');
  const formattedLastItemDate = lastItemWeekEndDate ?? (deferredTo || '');

  const minimapStart =
    from && isBefore(from, formattedFirstItemDate) ? from : formattedFirstItemDate;
  const minimapEnd = to && isAfter(to, formattedLastItemDate) ? to : formattedLastItemDate;

  const deferredMinimapStart =
    deferredFrom && isBefore(deferredFrom, formattedFirstItemDate)
      ? deferredFrom
      : formattedFirstItemDate;
  const deferredMinimapEnd =
    deferredTo && isAfter(deferredTo, formattedLastItemDate) ? deferredTo : formattedLastItemDate;

  return {
    deferredFrom,
    deferredTo,
    firstItemWeekStartDate,
    lastItemWeekEndDate,
    minimapStart,
    minimapEnd,
    deferredMinimapStart,
    deferredMinimapEnd,
  };
};
